import { CircularProgress, Grid } from '@mui/material'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useTitle } from 'hooks'
import { getPipelines, SET_PIPELINE, updatePipelineItemOrder } from 'store/actions/pipeline'
import { setCardIsUpdating } from 'store/actions/pipelineCards'
import { redirectTo } from 'store/actions/user'
import { BillingPlanEnum } from 'store/reducers/user'
import { selectIsPipelineLoading, selectPipeline } from 'store/selectors/pipeline'
import { selectPipelineStages } from 'store/selectors/pipelineStages'
import { selectHasExecutePermissions, selectUser } from 'store/selectors/user'
import { useHotjar } from 'utils/hotjar'
import { trackChangeFunderPipelineStage, trackPageView } from 'utils/mixpanel'

import PipelineFilters from 'components/PipelineFilters'
import PipelineStage from 'components/PipelineStage'
import PipelineTable from 'components/PipelineTable'
import SearchPageViewChanger from 'pages/search/components/SearchPageViewChanger'

const LS_KEY = 'pipelineView'
const pipelineViews = {
  EXPANDED: 'expanded',
  COLLAPSED: 'collapsed'
}

const Pipeline = () => {
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const isLoading = useSelector(selectIsPipelineLoading)
  const { results: pipeline } = useSelector(selectPipeline)
  const { results: pipelineStages } = useSelector(selectPipelineStages)
  const { billingPlan } = useSelector(selectUser)
  const dispatch = useDispatch()

  const [currentlyDragging, setCurrentlyDragging] = useState(false)
  const [pipelineView, setPipelineView] = useState(localStorage[LS_KEY] || pipelineViews.EXPANDED)
  const [pipelineFilter, setPipelineFilter] = useState()
  const [archivedFilter, setArchivedFilter] = useState(false)
  const [showOnlyHidden, setShowOnlyHidden] = useState(false)
  const [resultsPerPage, setResultsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const columnSize = pipelineView === pipelineViews.EXPANDED ? 12 : 9
  const hasExecutePermissions = useSelector(selectHasExecutePermissions)

  const pipelineData = pipeline.filter(item => item.pipelineStage && !item.hidden)
  const archivedPipelineData = pipeline.filter(item => item.archived)
  const hiddenPipelineData = pipeline.filter(item => item.hidden)

  const isExportAllowed =
    pipelineData.length > 0 && billingPlan && billingPlan !== BillingPlanEnum.MONTHLY

  useTitle(t => t.pipeline.pipeline)

  useEffect(() => {
    if (!hasExecutePermissions) {
      dispatch(redirectTo('/search', { replace: true }))
    }
  }, [hasExecutePermissions])

  useHotjar()

  useEffect(() => {
    trackPageView()
  }, [])

  useEffect(() => {
    dispatch(getPipelines())
  }, [])

  const opportunityFilter = item => {
    let included = true

    if (pipelineView === pipelineViews.EXPANDED) {
      return included && !item.hidden
    }

    included = included && (pipelineFilter ? item.pipelineStage?.id === pipelineFilter : true)

    return included
  }

  const updateDb = async (pipelineId, opportunityId, newPosition) => {
    await dispatch(setCardIsUpdating(true))

    const success = await dispatch(updatePipelineItemOrder(pipelineId, opportunityId, newPosition))

    await dispatch(setCardIsUpdating(false))

    return success
  }

  const onDragEnd = async ({ reason, destination, draggableId }) => {
    if (!destination || reason === 'CANCEL') return

    const opportunityId = Number(draggableId)
    const pipelineId = Number(destination.droppableId)
    const pipeline = pipelineStages.find(item => item.id === pipelineId)
    const newPosition = destination.index

    const opportunity = pipelineData.find(item => item.id === opportunityId)
    const opportunitiesInPipeline = pipelineData
      .filter(item => !item.hidden && !item.archived)
      .filter(item => item.pipelineStage.id === pipelineId && item.id !== opportunityId)
      .sort((a, b) => a.pipelineStageOrder - b.pipelineStageOrder)

    const prevStageId =
      (opportunity.pipelineStage && opportunity.pipelineStage.id) || opportunity.pipelineStage

    if (pipelineId === prevStageId && newPosition === opportunity.pipelineStageOrder) return

    const updatedPipeline = [
      ...opportunitiesInPipeline.slice(0, newPosition),
      opportunity,
      ...opportunitiesInPipeline.slice(newPosition)
    ].map((item, index) => ({
      ...item,
      pipelineStage: pipeline,
      pipelineStageOrder: index
    }))

    const updatedPipelineData = [
      ...pipelineData.filter(
        item => item.pipelineStage.id !== pipelineId && item.id !== opportunityId
      ),
      ...updatedPipeline,
      ...archivedPipelineData,
      ...hiddenPipelineData
    ]

    dispatch({ type: SET_PIPELINE, payload: { data: { results: updatedPipelineData } } })
    updateDb(pipelineId, opportunityId, newPosition)

    await trackChangeFunderPipelineStage({
      funderId: opportunity.funder.id,
      prevStageId,
      stageId: pipelineId
    })
  }

  const togglePipelineView = newView => {
    localStorage.setItem(LS_KEY, newView)
    setPipelineView(newView)
  }

  if (isLoading)
    return (
      <div className="main refresh-container">
        <CircularProgress size={60} thickness={5} color="#4c9eff" />
      </div>
    )

  return (
    <div className={`main Pipeline_container ${pipelineView}`}>
      {pipelineData.length === 0 && (
        <div className="profile-special-notes">
          <div className="profile-special-notes-note">{t.pipeline.pipelineEmpty}</div>
        </div>
      )}
      <Grid container className="Pipeline__container">
        {pipelineView === 'collapsed' && (
          <Grid size={{ xs: 12, md: 3 }}>
            <PipelineFilters
              pipelineStages={pipelineStages}
              counts={_(pipelineData)
                // item.hidden can be false or null, by design
                .filter(item => (showOnlyHidden ? item.hidden : !item.hidden))
                .groupBy('pipelineStage.id')
                .value()}
              pipelineFilter={pipelineFilter}
              applyFilter={val => {
                setCurrentPage(1)
                setArchivedFilter(null)
                setPipelineFilter(val)
              }}
              showOnlyHidden={showOnlyHidden}
              setShowOnlyHidden={val => {
                setCurrentPage(1)
                setPipelineFilter(null)
                setShowOnlyHidden(val)
              }}
              archivedCount={archivedPipelineData.length}
              archivedFilter={archivedFilter}
              setArchivedFilter={() => {
                setCurrentPage(1)
                setPipelineFilter(null)
                setArchivedFilter(true)
              }}
            />
          </Grid>
        )}
        <Grid size={{ md: columnSize }}>
          <Grid container>
            <h3>{t.pipeline.pipeline}</h3>
            <p>{`${pipelineData.filter(opportunityFilter).length} ${
              pipelineData.filter(opportunityFilter).length !== 1
                ? t.pipeline.prospectiveFunders
                : t.pipeline.prospectiveFunder
            }`}</p>
            <div className="tw-flex-grow" />
            <SearchPageViewChanger
              onClick={togglePipelineView}
              expandedActive={pipelineView === pipelineViews.EXPANDED ? 'active' : ''}
              collapsedActive={pipelineView === pipelineViews.COLLAPSED ? 'active' : ''}
              pipelineData={pipelineData}
              download={isExportAllowed}
            />
          </Grid>
          <Grid container className="Pipeline">
            {pipelineView === 'expanded' &&
              _(pipelineStages)
                .orderBy('order')
                .map((stage, stageId) => (
                  <div className="Pipeline__wrapper" key={stageId}>
                    <PipelineStage
                      id={stage.id}
                      name={stage.name}
                      opportunities={pipelineData.filter(
                        item => !item.hidden && item.pipelineStage.id === stage.id
                      )}
                      currentlyDragging={currentlyDragging}
                      setCurrentlyDragging={setCurrentlyDragging}
                      onDrop={onDragEnd}
                    />
                  </div>
                ))
                .value()}
            {pipelineView === 'collapsed' && (
              <PipelineTable
                filteredOpportunities={
                  archivedFilter
                    ? archivedPipelineData
                    : showOnlyHidden
                    ? hiddenPipelineData.filter(opportunityFilter)
                    : pipelineData.filter(opportunityFilter)
                }
                resultsPerPage={resultsPerPage}
                setResultsPerPage={setResultsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default Pipeline
